import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";
import { Badge } from "@/shared/ui/badge";
import { Switch } from "@/shared/ui/switch";
import { Settings, Activity, CheckCircle2, Clock } from "lucide-react";
import type { ControlAction } from "../types";

interface ControlTabProps {
  showMockData: boolean;
  mockControlActions: ControlAction[];
}

export const ControlTab = ({ showMockData, mockControlActions }: ControlTabProps) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Control Settings */}
      <Card className="lg:col-span-1">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Control Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">AI Auto-Control</p>
              <p className="text-xs text-muted-foreground">Allow AI to adjust parameters</p>
            </div>
            <Switch checked={true} />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">Human Approval</p>
              <p className="text-xs text-muted-foreground">Require approval for changes</p>
            </div>
            <Switch checked={false} />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">Emergency Stop</p>
              <p className="text-xs text-muted-foreground">Halt production on critical</p>
            </div>
            <Switch checked={true} />
          </div>
          <div className="pt-4 border-t">
            <p className="text-sm text-muted-foreground mb-2">Adjustment Limits</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Max Voltage Change</span>
                <span className="font-mono">±2.0V</span>
              </div>
              <div className="flex justify-between">
                <span>Max Speed Change</span>
                <span className="font-mono">±5%</span>
              </div>
              <div className="flex justify-between">
                <span>Max Temp Change</span>
                <span className="font-mono">±10°C</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Control Action Log */}
      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Control Action Log
          </CardTitle>
          <CardDescription>Complete audit trail of AI-initiated machine adjustments</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Timestamp</TableHead>
                <TableHead>Station</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Trigger</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {showMockData ? mockControlActions.map((action) => (
                <TableRow key={action.id}>
                  <TableCell className="font-mono text-xs">{action.timestamp}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{action.station}</Badge>
                  </TableCell>
                  <TableCell className="max-w-xs truncate">{action.action}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{action.trigger}</TableCell>
                  <TableCell>
                    <Badge variant={action.status === 'executed' ? 'default' : 'secondary'}>
                      {action.status === 'executed' && <CheckCircle2 className="h-3 w-3 mr-1" />}
                      {action.status === 'pending' && <Clock className="h-3 w-3 mr-1" />}
                      {action.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    No control actions recorded
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
